import { CaretDown } from "@phosphor-icons/react";
import {
  Button,
  Select,
  SelectItem,
  SelectPopover,
  SelectProvider,
} from "@replicate/ui";
import { capitalize } from "lodash-es";
import { memo, type ReactNode } from "react";
import type { BillingConfigTiers } from "../../types";

interface FilterControlsProps {
  tiers: BillingConfigTiers;
  filters: Record<string, string | undefined>;
  onFilterChange(key: string, value: string | undefined): void;
}

function ClearFiltersButton({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button size="sm" variant="filled" onClick={onClick} className={className}>
      Clear filters
    </Button>
  );
}

export const FilterControls = memo(function FilterControls({
  tiers,
  filters,
  onFilterChange,
}: FilterControlsProps): ReactNode {
  const criteriaOptions = getCriteriaOptions(tiers);
  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined);

  const clearFilters = () => {
    for (const key in filters) {
      onFilterChange(key, undefined);
    }
  };

  return (
    <div className="space-y-4 bg-white dark:bg-black p-4 pb-5 rounded-sm shadow border">
      {hasActiveFilters && Object.keys(criteriaOptions).length > 5 && (
        <div className="pt-2">
          <ClearFiltersButton onClick={clearFilters} className="w-full" />
        </div>
      )}
      {Object.entries(criteriaOptions).map(([optionKey, optionValues]) => {
        const currentValue = filters[optionKey];

        return (
          <div key={optionKey} className="space-y-2">
            <label className="text-r8-sm font-medium inline-block">
              {capitalize(optionKey)}
            </label>
            <SelectProvider
              value={currentValue === undefined ? "" : currentValue}
              setValue={(value) => {
                onFilterChange(optionKey, value === "" ? undefined : value);
              }}
            >
              <Select
                id={optionKey}
                render={
                  <Button
                    size="sm"
                    variant="outlined"
                    endIcon={<CaretDown />}
                    className="w-full justify-between"
                  >
                    {currentValue}
                  </Button>
                }
              >
                {currentValue}
              </Select>
              <SelectPopover sameWidth>
                <SelectItem value="">Any</SelectItem>
                {Array.from(optionValues).map((value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectPopover>
            </SelectProvider>
          </div>
        );
      })}
      {hasActiveFilters && (
        <div className="pt-2">
          <ClearFiltersButton onClick={clearFilters} className="w-full" />
        </div>
      )}
    </div>
  );
});

function getCriteriaOptions(
  tiers: BillingConfigTiers
): Record<string, Set<string>> {
  return tiers.reduce<Record<string, Set<string>>>((acc, tier) => {
    for (const criterion of tier.criteria) {
      const filterKey = criterion.title;

      if (!acc[filterKey]) {
        acc[filterKey] = new Set();
      }

      acc[filterKey].add(criterion.description);
    }

    return acc;
  }, {});
}
